import { ref } from 'vue';
import httpService from '../services/httpService';
import { log } from '../utils/DebugLogger';
import type { VendasServicos_ItemRow } from '../contracts/VendasServicos_ItemRow';
import type { VendasServicos_KpiRow } from '../contracts/VendasServicos_KpiRow';

/**
 * Venda por matriz serviço × prioridade (chamado 2026-0086).
 *
 * Itens de serviço de OS não canceladas, criados no período, nas franquias do
 * filtro. Sem valores: os KPIs são soma da quantidade e contagem de itens por
 * (serviço, prioridade), calculados pelo backend sobre a mesma lista.
 * As datas são date-only (YYYY-MM-DD) e vão sem conversão de fuso.
 */
export interface OpcaoPrioridade {
  ID: number | string;
  Name: string;
}

export function useVendasServicos() {
  const itens = ref<VendasServicos_ItemRow[]>([]);
  const kpis = ref<VendasServicos_KpiRow[]>([]);
  const prioridades = ref<OpcaoPrioridade[]>([]);
  const carregando = ref<boolean>(false);
  const erro = ref<string | null>(null);

  async function carregar(dataInicio: string, dataFim: string, prioridadeId: number | null = null): Promise<void> {
    carregando.value = true;
    erro.value = null;

    try {
      const params: Record<string, string | number> = { data_inicio: dataInicio, data_fim: dataFim };
      if (prioridadeId !== null) {
        params.prioridade_id = prioridadeId;
      }
      const response = await httpService.get('/index.php/relatorios/vendas_servicos/lista', {
        params,
        responseContract: 'VendasServicos_ListaResponse',
      });

      itens.value = Array.isArray(response?.dados?.itens) ? response.dados.itens : [];
      kpis.value = Array.isArray(response?.dados?.kpis) ? response.dados.kpis : [];
    } catch (e) {
      erro.value = e instanceof Error ? e.message : String(e);
      itens.value = [];
      kpis.value = [];
    } finally {
      carregando.value = false;
    }
  }

  async function carregarPrioridades(): Promise<void> {
    try {
      const response = await httpService.get('/index.php/so/priorities/lista', {
        responseContract: 'Priorities_ListaResponse',
      });
      const linhas = Array.isArray(response?.dados) ? response.dados : [];
      prioridades.value = linhas.map((p) => ({ ID: p.so_priority_id, Name: p.so_priority_name }));
    } catch (e) {
      // Sem a lista, o filtro de prioridade fica vazio; o relatório continua.
      log('useVendasServicos', 'carregarPrioridades', 'falha ao carregar prioridades', e);
      prioridades.value = [];
    }
  }

  return { itens, kpis, prioridades, carregando, erro, carregar, carregarPrioridades };
}
